import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, User, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';

export type AudienceType = 'jobseeker' | 'recruiter';

interface HeaderProps {
  activeAudience?: AudienceType;
  onToggle?: (audience: AudienceType) => void;
  showLandingNav?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  activeAudience = 'jobseeker',
  onToggle,
  showLandingNav = false,
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, signOut, loading } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    await signOut();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMenuOpen(false);
  };

  const userName = user?.user_metadata?.full_name || user?.email;
  const userEmail = user?.email;
  const userPicture = user?.user_metadata?.avatar_url;
  const userType = user?.user_metadata?.user_type || 'candidate';
  const isHirer = userType === 'company' || userType === 'hirer';

  // Show section links only when in recruiter mode and on landing page
  const showSectionLinks = showLandingNav && activeAudience === 'recruiter' && !user;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-2 md:pt-4 px-2 md:px-4">
      <div className="w-full max-w-6xl bg-white/85 backdrop-blur-lg rounded-2xl shadow-lg shadow-slate-900/5 border border-slate-200/50">
        <div className="flex items-center justify-between h-16 md:h-18 px-4 md:px-6">
          {/* Logo */}
          <div
            className="flex items-center space-x-2 md:space-x-3 cursor-pointer group flex-shrink-0"
            onClick={handleHomeClick}
          >
            <img
              src="/JobVitalLogo.png"
              alt="JobVital Logo"
              className="h-8 md:h-10 w-auto group-hover:scale-105 transition-transform duration-200"
            />
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              JobVital
            </span>
          </div>

          {/* Center: Section Links (Recruiter Mode Only) */}
          {showSectionLinks && (
            <nav className="hidden lg:flex items-center gap-1">
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 rounded-lg transition-all duration-200"
              >
                How It Works
              </button>
              {/* <button
                onClick={() => scrollToSection('pricing')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 rounded-lg transition-all duration-200"
              >
                Pricing
              </button> */}
            </nav>
          )}

          {/* Right Side: Auth */}
          <div className="hidden md:flex items-center gap-3">
            {/* Auth Buttons */}
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-200 border-t-blue-600"></div>
              </div>
            ) : !user ? (
              <>
                <button
                  onClick={() => navigate('/hiring-login')}
                  className="px-4 py-2 text-sm text-slate-700 font-medium hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-200"
                >
                  Log in
                </button>
                <button
                  onClick={() => navigate('/hiring-signup')}
                  className="px-5 py-2 text-sm text-white font-medium bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Sign Up
                </button>
              </>
            ) : (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${isHirer
                    ? 'bg-violet-100 text-violet-700'
                    : 'bg-blue-100 text-blue-700'
                    }`}>
                    {isHirer ? 'Hiring' : 'Job Seeker'}
                  </span>

                  {userPicture ? (
                    <img
                      src={userPicture}
                      alt={userName || 'User'}
                      className="w-8 h-8 rounded-full ring-2 ring-slate-100"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl shadow-slate-900/10 border border-slate-100 py-2 z-50"
                  >
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-900">{userName}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{userEmail}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Log out
                    </button>
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-slate-600" />
              ) : (
                <Menu className="w-6 h-6 text-slate-600" />
              )}
            </button>

            {/* Mobile Menu */}
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-4 right-4 bg-white shadow-xl shadow-slate-900/10 rounded-2xl mt-2 p-4 z-50 border border-slate-100"
              >
                {/* Mobile Section Links */}
                {showSectionLinks && (
                  <div className="mb-4 pb-4 border-b border-slate-100 space-y-1">
                    <button
                      onClick={() => scrollToSection('how-it-works')}
                      className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                    >
                      How It Works
                    </button>
                    {/* <button
                      onClick={() => scrollToSection('pricing')}
                      className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                    >
                      Pricing
                    </button> */}
                  </div>
                )}

                {/* Mobile Auth */}
                <div className="flex flex-col gap-2">
                  {loading ? (
                    <div className="flex items-center justify-center gap-2 py-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-200 border-t-blue-600"></div>
                    </div>
                  ) : !user ? (
                    <>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          navigate('/hiring-login');
                        }}
                        className="w-full py-3 text-slate-700 font-medium border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                      >
                        Log in
                      </button>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          navigate('/hiring-signup');
                        }}
                        className="w-full py-3 text-white font-medium bg-slate-900 rounded-xl hover:bg-slate-800 transition-colors"
                      >
                        Sign Up
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl mb-2">
                        {userPicture ? (
                          <img
                            src={userPicture}
                            alt={userName || 'User'}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{userName}</p>
                          <p className="text-sm text-slate-500 truncate">{userEmail}</p>
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${isHirer
                            ? 'bg-violet-100 text-violet-700'
                            : 'bg-blue-100 text-blue-700'
                            }`}>
                            {isHirer ? 'Hiring' : 'Job Seeker'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full py-3 text-red-600 font-medium border border-red-200 rounded-xl hover:bg-red-50 transition-colors"
                      >
                        Log out
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
